import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { Cryptocoin, Fiat } from "@models";
import { FIAT_EURO_ID, TOKEN_BEST_ID } from "@helpers/constants";
import { CryptocoinService, FiatService } from "@main-services/currency";
import { Tradable } from "@models/interfaces/tradable";
import { Index } from "@models/asset/index/index.model";
import { convertTradableToFiat } from "@helpers/tradable-convert";
import { ITBSwapData } from "@modules/shared/components/instant-trade-bonus/entitites/itb-swap";
import { ITBTrade } from "@modules/shared/components/instant-trade-bonus/entitites/itb-trade";
import { ITBStep } from "@modules/shared/components/instant-trade-bonus/entitites/itb-step";

@Component({
	selector: "instant-trade-bonus",
	templateUrl: "./instant-trade-bonus.component.html",
	styleUrls: ["./instant-trade-bonus.component.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: false
})
export class InstantTradeBonusComponent implements OnInit {
	@Input() step: ITBStep = null;
	@Input() public set amount(s: string) {
		this.itbAmount = s;
	}
	@Input() public set tradeData(d: ITBTrade) {
		const result = this.calculateTradeData(d);
		this.itbAmount = result;
	}
	@Input() public set swapData(d: ITBSwapData) {
		const result: string = this.calculateSwapAmount(d);
		this.itbAmount = result;
	}
	@Input() public showLoading: boolean = false;

	public itbAmount: string = "";
	public messageKey: string = "";
	@Input() isSaving: boolean = false;

	private best: Cryptocoin = null;
	private euroFiat: Fiat = null; // Api always returns the ITB ratio in euro. So we calculate using euro too.

	public constructor(
		private cryptocoinService: CryptocoinService,
		private fiatService: FiatService
	) {}

	public ngOnInit(): void {
		this.setCurrencies();
		this.messageKey = this.getMessageKey();
	}

	// Utility
	public getBest(): Cryptocoin {
		return this.best;
	}

	protected setCurrencies(): void {
		this.best = this.cryptocoinService.getItemById(TOKEN_BEST_ID);
		this.euroFiat = this.fiatService.getItemById(FIAT_EURO_ID);
	}

	// Visibility
	public showItb(): boolean {
		return (!!this.step && !!this.itbAmount && +this.itbAmount !== 0) || this.showLoading;
	}

	private getMessageKey(): string {
		switch (this.step) {
			case ITBStep.SUCCESS:
				return "instant_trade_bonus_success";
			default:
				return "instant_trade_bonus_amount";
		}
	}

	// Calculus
	private calculateSwapAmount(d: ITBSwapData): string {
		if (
			!d?.source?.tradable ||
			!d?.source?.inputAmount ||
			!d?.source?.itbRatio ||
			!d?.target?.tradable ||
			!d?.target?.inputAmount ||
			!d?.target?.itbRatio
		) {
			return "";
		}
		const totalSourceITB: number =
			(this.convertToFiat(d.source.inputAmount, d.source.tradable, this.euroFiat) * +d.source.itbRatio) /
			this.best?.getPrice();
		const totalTargetITB: number =
			(this.convertToFiat(d.target.inputAmount, d.target.tradable, this.euroFiat) * +d.target.itbRatio) /
			this.best?.getPrice();

		return "" + (totalSourceITB + totalTargetITB);
	}

	private convertToFiat(amount: number, tradable: Tradable, fiat: Fiat): number {
		if (tradable instanceof Index) {
			return amount;
		}
		return convertTradableToFiat(tradable, amount, fiat);
	}

	private calculateTradeData(d: ITBTrade): string {
		if (!d?.amount || !d?.itbRate || !this.best) {
			return "";
		}
		return "" + (+d.amount * +d.itbRate) / this.best.getPrice();
	}

	public isSuccessStep(): boolean {
		return this.step === ITBStep.SUCCESS;
	}
}
